import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@eluve/api-client-provider';
import {
  CreateUserInviteRequest,
  createUserInviteRequestSchema,
} from '@eluve/api-contract';
import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  HStack,
  Icon,
  Input,
  NewButton,
  ToggleGroup,
  ToggleGroupItem,
  VStack,
  useToast,
} from '@eluve/components';
import { useTenantId } from '@eluve/frontend-appointment-hooks';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  useIsFeatureFlagEnabled,
  useTenantLocations,
} from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

const defaultFormValues = {
  email: '',
  role: 'tenant-member' as const,
  firstName: '',
  lastName: '',
  locationId: undefined,
};

type InviteForm = CreateUserInviteRequest;

export const InviteUserModal: FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const api = useApiClient();

  const tenantId = useTenantId();
  const userId = useUserIdFromSession();
  const locationsEnabled = useIsFeatureFlagEnabled('LOCATIONS');
  const { locations } = useTenantLocations({ skip: !locationsEnabled });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isDialogOpen, toggleDialog } = useDialog(false);
  const toast = useToast();

  const analytics = useAnalytics('organization_invites');

  const form = useForm<InviteForm>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(createUserInviteRequestSchema),
  });

  const handleOpenChange = () => {
    toggleDialog();
    form.reset(defaultFormValues);
    setIsSubmitting(false);
  };

  const onSubmit = async (values: InviteForm) => {
    if (!values.email.trim()) {
      return;
    }

    setIsSubmitting(true);

    const response = await api.tenantAdminUsers.createInvite({
      params: {
        tenantId,
      },
      body: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        locationId: values.locationId,
        role: values.role,
      },
    });

    if (response.status === 200) {
      onSuccess();
    } else {
      toast.error('Invitation could not be sent.');
    }

    handleOpenChange();
  };

  useEffect(() => {
    if (isDialogOpen && tenantId && userId) {
      analytics.capture('modal_open', {
        tenantId,
        userId,
      });
    }
  }, [isDialogOpen, tenantId, userId, analytics]);

  return (
    <>
      <NewButton
        icon={{ name: 'UserRoundPlus' }}
        onClick={toggleDialog}
        text="Invite User"
      />
      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <Form {...form}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <VStack className="my-4" gap={4}>
                <FormField
                  control={form.control}
                  disabled={isSubmitting}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>User Role</FormLabel>
                      <FormControl>
                        <ToggleGroup
                          disabled={isSubmitting}
                          onValueChange={(value) => {
                            if (value) {
                              field.onChange(value);
                            }
                          }}
                          type="single"
                          value={field.value}
                          variant="outline"
                        >
                          <ToggleGroupItem value="tenant-admin">
                            Admin
                          </ToggleGroupItem>
                          <ToggleGroupItem value="tenant-member">
                            Member
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormDescription>
                        Admins can view all patients and appointments, as well
                        as manage users and settings for the organization. Note:
                        you can change the role of a user later.
                      </FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  disabled={isSubmitting}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="john.doe@example.com" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <HStack gap={4}>
                  <FormField
                    control={form.control}
                    disabled={isSubmitting}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    disabled={isSubmitting}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </HStack>
                {locationsEnabled && (
                  <FormField
                    control={form.control}
                    name="locationId"
                    render={({ field }) => {
                      const selectedLocation = locations.find(
                        (location) => location.id === field.value,
                      );
                      return (
                        <FormItem className="w-full">
                          <FormLabel>Location</FormLabel>
                          <FormControl>
                            <Combobox disabled={isSubmitting}>
                              <ComboboxSelectButton
                                className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
                                dropdownIcon={
                                  <Icon
                                    className="ml-2 opacity-50"
                                    name="ChevronDown"
                                    size="xs"
                                  />
                                }
                              >
                                {selectedLocation?.name ?? 'Select a location'}
                              </ComboboxSelectButton>
                              <ComboboxDropdown>
                                {locations.map((location) => (
                                  <ComboboxOption
                                    key={location.id}
                                    onSelect={() => field.onChange(location.id)}
                                  >
                                    <ComboboxSelectCheck
                                      className="mr-2"
                                      selected={
                                        selectedLocation?.id === location.id
                                      }
                                    />
                                    {location.name}
                                  </ComboboxOption>
                                ))}
                              </ComboboxDropdown>
                            </Combobox>
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                )}
              </VStack>
              <DialogFooter className="mt-8">
                <NewButton
                  loading={isSubmitting}
                  submit
                  text="Send Invitation"
                />
              </DialogFooter>
            </form>
          </DialogContent>
        </Form>
      </Dialog>
    </>
  );
};
